'use client';

import { useTRPC } from '@/lib/trpc/provider';
import { useAuthStore, logoutUser } from '@/hooks/use-auth';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { flushBeforeProfileSwitch } from '@/lib/student/before-profile-switch';

export function useGetProfile() {
  const trpc = useTRPC();
  const query = useQuery(trpc.user.getProfile.queryOptions());

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useUpdateProfile() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const setTimezone = useAuthStore((s) => s.setTimezone);

  return useMutation(
    trpc.user.updateProfile.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.getProfile.queryKey() });
        // Sync the cached auth-store timezone so client-side time projection
        // (e.g. the enrollment catalog) reflects the change immediately instead
        // of waiting for a token refresh / re-login.
        if (variables?.timezone) setTimezone(variables.timezone);
        toast.success('Profile updated');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useUpdateEmail() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.user.updateEmail.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.getProfile.queryKey() });
        toast.success('Email updated');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useChangePassword() {
  const trpc = useTRPC();

  return useMutation(
    trpc.user.changePassword.mutationOptions({
      onSuccess: () => {
        toast.success('Password changed. Please log in again.');
        void logoutUser();
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useChildProfiles() {
  const trpc = useTRPC();
  const query = useQuery(trpc.user.listChildren.queryOptions());

  return {
    children: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateChild() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.user.createChild.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.listChildren.queryKey() });
        toast.success('Profile created');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useCreateChildWithConsent() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.user.createChildWithConsent.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.listChildren.queryKey() });
        toast.success('Profile created with consent');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useUpdateChild() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.user.updateChild.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.listChildren.queryKey() });
        toast.success('Profile updated');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useDeleteChild() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.user.deleteChild.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.listChildren.queryKey() });
        toast.success('Profile removed');
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
}

export function useSwitchProfile() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setProfile = useAuthStore((s) => s.setProfile);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  return useMutation({
    // Accepts a bare profileId (back-compat) or an object with an optional
    // `redirectTo` so callers can land somewhere specific after the switch
    // (e.g. a child's placement test page).
    mutationFn: async (
      arg: string | { profileId: string; redirectTo?: string }
    ) => {
      const profileId = typeof arg === 'string' ? arg : arg.profileId;
      // Exit work (the reader's reading time) goes out under THIS session,
      // before the switch swaps it — see `before-profile-switch.ts`.
      await flushBeforeProfileSwitch();
      const response = await fetch('/api/auth/switch-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ profileId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message ?? 'Unable to switch profile');
      }

      return response.json();
    },
    onSuccess: (data, arg) => {
      if (user) {
        setAuth(data.accessToken, {
          ...user,
          role: data.profile.role,
        });
        setProfile(data.profile.profileId ?? null, data.profile.firstName ?? null, data.profile.gradeLevel ?? null);
      }
      // C2 fix: switch-profile now rotates the refresh token server-side.
      // Navigate via full reload so AuthProvider picks up the new token state.
      // Route to the role-appropriate home — navigating a student to /dashboard causes a
      // double-redirect through middleware (PARENT_ONLY_PATHS blocks students).
      const redirectTo = typeof arg === 'string' ? undefined : arg.redirectTo;
      if (typeof window !== 'undefined') {
        window.location.href =
          redirectTo ??
          (data.profile.role === 'student' ? '/student' : '/dashboard');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message, { duration: 8000 });
    },
  });
}

export function useSwitchToParent() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setProfile = useAuthStore((s) => s.setProfile);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  return useMutation({
    // Optional `redirectTo` lets callers land somewhere specific in the parent
    // app after switching back (defaults to the dashboard).
    mutationFn: async (arg?: { redirectTo?: string }) => {
      // As above: the child's exit work is sent before their session ends.
      await flushBeforeProfileSwitch();
      const response = await fetch('/api/auth/switch-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ profileId: null }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message ?? 'Unable to switch to parent view');
      }

      const data = await response.json();
      return { ...data, redirectTo: arg?.redirectTo };
    },
    onSuccess: (data) => {
      if (user) {
        setAuth(data.accessToken, {
          ...user,
          role: data.profile.role,
        });
        setProfile(null, null, null);
      }
      // C2 fix: switch-profile now rotates the refresh token server-side.
      if (typeof window !== 'undefined') {
        window.location.href = data.redirectTo ?? '/dashboard';
      }
    },
    onError: (error: Error) => {
      toast.error(error.message, { duration: 8000 });
    },
  });
}
